import type { Actor } from './actors';
import type { Resolution, TicketStatus } from './enums';
import { DomainError } from './errors';

/**
 * WF-03 transition matrix. A status move is a named domain action with guards, never an
 * unrestricted column update. No other transition is permitted.
 */
export const TICKET_ACTIONS = [
  'accept',
  'reject',
  'claim',
  'mark_ready',
  'release_claim',
  'reconsider',
  'rework_todo',
  'rework_in_progress',
  'close_done',
  'reopen',
] as const;
export type TicketAction = (typeof TICKET_ACTIONS)[number];

type ActorClass = 'admin' | 'ai' | 'owner' | 'system';

interface TransitionRule {
  from: readonly TicketStatus[];
  to: TicketStatus;
  resolution?: Resolution;
  actors: readonly ActorClass[];
  label: string;
}

export const TRANSITIONS: Record<TicketAction, TransitionRule> = {
  accept: { from: ['pending'], to: 'todo', actors: ['admin'], label: 'Accept current scope' },
  reject: {
    from: ['pending', 'todo', 'in_progress', 'pending_release'],
    to: 'closed',
    resolution: 'rejected',
    actors: ['admin'],
    label: 'Reject',
  },
  claim: { from: ['todo'], to: 'in_progress', actors: ['admin', 'ai'], label: 'Start implementation' },
  mark_ready: {
    from: ['in_progress'],
    to: 'pending_release',
    actors: ['owner', 'admin'],
    label: 'Mark pending release',
  },
  release_claim: { from: ['in_progress'], to: 'todo', actors: ['owner', 'admin'], label: 'Return to Todo' },
  reconsider: {
    from: ['todo', 'in_progress', 'pending_release'],
    to: 'pending',
    actors: ['admin'],
    label: 'Reconsider scope',
  },
  rework_todo: { from: ['pending_release'], to: 'todo', actors: ['admin'], label: 'Rework (queue AI)' },
  rework_in_progress: {
    from: ['pending_release'],
    to: 'in_progress',
    actors: ['admin'],
    label: 'Rework (admin ownership)',
  },
  close_done: {
    from: ['pending_release'],
    to: 'closed',
    resolution: 'done',
    actors: ['system'],
    label: 'Close as done',
  },
  reopen: { from: ['closed'], to: 'pending', actors: ['admin', 'system'], label: 'Reopen' },
};

function actorClasses(actor: Actor, isCurrentOwner: boolean): Set<ActorClass> {
  const classes = new Set<ActorClass>();
  if (actor.kind === 'human' && actor.role === 'admin') classes.add('admin');
  if (actor.kind === 'ai_service') classes.add('ai');
  if (actor.kind === 'system') classes.add('system');
  if (isCurrentOwner) classes.add('owner');
  return classes;
}

export function isTransitionAllowed(from: TicketStatus, to: TicketStatus): boolean {
  return Object.values(TRANSITIONS).some((r) => r.to === to && r.from.includes(from));
}

/** Validates the status precondition and actor class for an action; guards specific to the action are separate. */
export function checkTransition(
  action: TicketAction,
  status: TicketStatus,
  actor: Actor,
  opts: { isCurrentOwner?: boolean } = {},
): TransitionRule {
  const rule = TRANSITIONS[action];
  if (!rule.from.includes(status)) {
    throw new DomainError(
      'invalid_transition',
      `"${rule.label}" is not available while the ticket is ${status.replace('_', ' ')}.`,
    );
  }
  const classes = actorClasses(actor, opts.isCurrentOwner ?? false);
  if (!rule.actors.some((c) => classes.has(c))) {
    throw new DomainError('forbidden', `You are not allowed to "${rule.label}" on this ticket.`);
  }
  return rule;
}

/** Actions whose status and actor-class preconditions hold; used to offer only valid moves in the UI. */
export function availableActions(status: TicketStatus, actor: Actor, isCurrentOwner = false): TicketAction[] {
  const classes = actorClasses(actor, isCurrentOwner);
  return TICKET_ACTIONS.filter((a) => {
    const r = TRANSITIONS[a];
    return r.from.includes(status) && r.actors.some((c) => classes.has(c));
  });
}

/** Board moves map a target column to the domain action that would produce it. */
export function actionsForMove(from: TicketStatus, to: TicketStatus): TicketAction[] {
  return TICKET_ACTIONS.filter((a) => TRANSITIONS[a].from.includes(from) && TRANSITIONS[a].to === to);
}
